import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Tooltip,
  useToast,
  Text,
  Button,
} from "@chakra-ui/react";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi";
import { FaEnvelope } from "react-icons/fa";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import ReferencesEditor from "./ReferencesEditor";
import ClassificationsEditor from "./ClassificationsEditor";
import AmountsEditor from "./AmountsEditor";
import DateTimesEditor from "./DateTimesEditor";
import RegisterEntrySelect from "./RegisterEntrySelect";
import CodesetSelectWithFilters from "./CodesetSelectWithFilters";
import ProductSignalSelect from "./ProductSignalSelect";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";

const SignalCreator = ({ product, contract }) => {
  const { get, post } = useAuthenticatedRequest();




  const toast = useToast();
  const [localProduct, setLocalProduct] = useState(product || {});
  const [localContract, setLocalContract] = useState(contract || {});

  const [signalName, setSignalName] = useState("");
  const [signalType, setSignalType] = useState("");
  const [signalClassCode, setSignalClassCode] = useState("");
  const [signalProductCode, setSignalProductCode] = useState("");
  const [signalContractId, setSignalContractId] = useState("");
  const [signalRoute, setSignalRoute] = useState("");
  const [signalObjectType, setSignalObjectType] = useState("");
  const [signalObjectId, setSignalObjectId] = useState("");
  const [signalParentObjectType, setSignalParentObjectType] = useState("");
  const [signalParentObjectId, setSignalParentObjectId] = useState("");
  const [signalStandardPayloadType, setSignalStandardPayloadType] = useState("");
  const [signalActivityPatternId, setSignalActivityPatternId] = useState("");
  const [transactionFunctionCode, setTransactionFunctionCode] = useState("");
  const [signalDataArray, setSignalDataArray] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);
  const [parties, setParties] = useState([]);
  const [partyLoading, setPartyLoading] = useState(true);
  const [selectedEntryId, setSelectedEntryId] = useState("");

  const [selectedProductSignal, setSelectedProductSignal] = useState(null);

  const emitSignal = async () => {
    if (!signalName || !signalType || !transactionFunctionCode) {
      toast({ title: "Please fill in all required fields", status: "warning" });
      return;
    }

    const payload = {
      _id: crypto.randomUUID(),
      name: signalName,
      type: signalType,
      classCode: signalClassCode,
      productCode: localProduct.code,
      contractId: localContract._id,
      objectType: signalObjectType,
      objectId: signalObjectId,
      parentObjectType: signalParentObjectType,
      parentObjectId: signalParentObjectId,
      transactionFunctionCode,
      payload: JSON.stringify(signalDataArray),
      data: signalDataArray,
    };

    try {
      console.log(
        "Submitting signal to endpoint:",
        `https://poc.mrc.ai/${product.code}/api/signals${signalRoute}`
      );
      console.log(
        "Submitting signal with payload:",
        JSON.stringify(payload, null, 2)
      );

      await post(
        `https://poc.mrc.ai/${product.code}/api/signals${signalRoute}`,
        payload
      );

      toast({ title: "Signal submitted", status: "success" });
    } catch (err) {
      toast({
        title: "Signal error",
        description: err.message,
        status: "error",
      });
    }
  };

  const addEntry = () => {
    const newEntry = {
      id: crypto.randomUUID(),
      created: new Date().toISOString(),
      parties: [],
      addresses: [],
      references: [],
      classifications: [],
      amounts: [],
      dateTimes: [],
      text: [],
    };
    setSignalDataArray((prev) => [...prev, newEntry]);
  };

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await get("https://directory.poc.mrc.ai/api/parties");
        setParties(res.data);
      } catch (err) {
        console.error("Failed to fetch parties:", err);
      } finally {
        setPartyLoading(false);
      }
    };

    const fetchRegisters = async () => {
      try {
        const res = await get("https://registers.poc.mrc.ai/api/registers");
        setRegisters(res.data);
      } catch (err) {
        console.error("Failed to fetch registers:", err);
      } finally {
        setRegisterLoading(false);
      }
    };

    fetchParties();
    fetchRegisters();
  }, []);

  // Track selected register for register entry dropdown
  const [selectedRegisterId, setSelectedRegisterId] = useState("");

  useEffect(() => {
    console.log("🛠 signalDataArray updated:", signalDataArray);
  }, [signalDataArray]);

  useEffect(() => {
    if (!selectedProductSignal) return;

    // These property names assume your product.signalRegister items look like:
    // { _id, name, type, transactionFunctionCode, template?: { data?: [...] } }
    setSignalName(selectedProductSignal.name || "");
    setSignalType(selectedProductSignal.type || "");
    setSignalClassCode(selectedProductSignal.classCode || "");
    setSignalRoute(selectedProductSignal.route || "");
    setSignalObjectType(selectedProductSignal.objectType || "");
    setSignalObjectId(selectedProductSignal.objectId || "");
    setSignalParentObjectType(selectedProductSignal.parentObjectType || "");
    setSignalParentObjectId(selectedProductSignal.parentObjectId || "");
    setSignalStandardPayloadType(
      selectedProductSignal.standardPayloadType || ""
    );
    setSignalActivityPatternId(selectedProductSignal.activityPatternId || "");
    setTransactionFunctionCode(
      selectedProductSignal.transactionFunctionCode || ""
    );
  }, [selectedProductSignal]);

  return (
    <Box p={6}>
      <HStack mb={4}>
        <Tooltip label="Back">
          <IconButton
            icon={<HiOutlineArrowNarrowLeft />}
            onClick={() => window.history.back()}
          />
        </Tooltip>
        <Heading size="md">Create Standard Signal</Heading>
        <Tooltip label="Send Signal">
          <IconButton icon={<FaEnvelope />} onClick={emitSignal} />
        </Tooltip>
      </HStack>

      <SimpleGrid columns={5} spacing={4}>
        <FormControl>
          <FormLabel>Name</FormLabel>
          <Input
            value={signalName}
            onChange={(e) => setSignalName(e.target.value)}
          />
        </FormControl>

        <ProductSignalSelect
          label="Product Signal"
          productCode={product?.code}
          selectedSignal={selectedProductSignal}
          onChange={(signal) => {
            setSelectedProductSignal(signal);
            setSignalType(signal?.type); // pull type out of the object
          }}
          size="md"
        />

        <CodesetSelect
          label="Function"
          codesetCode="SLC012"
          value={
            transactionFunctionCode
              ? { code: transactionFunctionCode, description: "" }
              : null
          }
          onChange={(val) => setTransactionFunctionCode(val.code)}
        />
      </SimpleGrid>

      <RegisterEntrySelect
        registers={registers}
        registerLoading={registerLoading}
        setSignalDataArray={setSignalDataArray}

      />

      <HStack alignItems="center" mb={2}>
        <Heading size="md">Signal Data</Heading>
        <IconButton icon={<HiOutlinePlus />} size="md" onClick={addEntry} />
      </HStack>

      {signalDataArray.map((signalData, signalDataIdx) => (
        <Box
          key={signalData.id}
          p={4}
          borderWidth="1px"
          borderRadius="md"
          mb={4}>
          <UuidPreview uuid={signalData.id} />
          <Heading size="md">Signal</Heading>
          <Box p={4} borderWidth="1px" borderRadius="md" mb={4}>
            <SimpleGrid columns={4} spacing={4}>
              <FormControl>
                <FormLabel>Product Code</FormLabel>
                <Input
                  value={signalData.productCode || ""}
                  onChange={(e) => {
                    const updated = [...signalDataArray];
                    updated[signalDataIdx].productCode = e.target.value;
                    setSignalDataArray(updated);
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Contract Id</FormLabel>
                <Input
                  value={signalData.contractId || ""}
                  onChange={(e) => {
                    const updated = [...signalDataArray];
                    updated[signalDataIdx].contractId = e.target.value;
                    setSignalDataArray(updated);
                  }}
                />
              </FormControl>
              <CodesetSelect
                label="Signal Type"
                codesetCode="B58"
                value={
                  signalData.type
                    ? { code: signalData.type, description: "" }
                    : null
                }
                onChange={(val) => {
                  const updated = [...signalDataArray];
                  updated[signalDataIdx].type = val.code;
                  setSignalDataArray(updated);
                }}
              />
              <CodesetSelect
                label="Signal Class"
                codesetCode="SLC010"
                value={
                  signalData.classCode
                    ? { code: signalData.classCode, description: "" }
                    : null
                }
                onChange={(val) => {
                  const updated = [...signalDataArray];
                  updated[signalDataIdx].classCode = val.code;
                  setSignalDataArray(updated);
                }}
              />
            </SimpleGrid>
            <SimpleGrid columns={4} spacing={4}>
              <CodesetSelect
                label="Object Type"
                codesetCode="B58"
                value={
                  signalData.objectType
                    ? { code: signalData.objectType, description: "" }
                    : null
                }
                onChange={(val) => {
                  const updated = [...signalDataArray];
                  updated[signalDataIdx].objectType = val.code;
                  setSignalDataArray(updated);
                }}
              />
              <FormControl>
                <FormLabel>Object Id</FormLabel>
                <Input
                  value={signalData.objectId || ""}
                  onChange={(e) => {
                    const updated = [...signalDataArray];
                    updated[signalDataIdx].objectId = e.target.value;
                    setSignalDataArray(updated);
                  }}
                />
              </FormControl>
              <CodesetSelect
                label="Parent Object Type"
                codesetCode="B58"
                value={
                  signalData.parentObjectType
                    ? { code: signalData.parentObjectType, description: "" }
                    : null
                }
                onChange={(val) => {
                  const updated = [...signalDataArray];
                  updated[signalDataIdx].parentObjectType = val.code;
                  setSignalDataArray(updated);
                }}
              />
              <FormControl>
                <FormLabel>Parent Object Id</FormLabel>
                <Input
                  value={signalData.parentObjectId || ""}
                  onChange={(e) => {
                    const updated = [...signalDataArray];
                    updated[signalDataIdx].parentObjectId = e.target.value;
                    setSignalDataArray(updated);
                  }}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={signalData.name || ""}
                  onChange={(e) => {
                    const updated = [...signalDataArray];
                    updated[signalDataIdx].name = e.target.value;
                    setSignalDataArray(updated);
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Quantity</FormLabel>
                <Input
                  value={signalData.quantity || ""}
                  onChange={(e) => {
                    const updated = [...signalDataArray];
                    updated[signalDataIdx].quantity = e.target.value;
                    setSignalDataArray(updated);
                  }}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing={4}>
              <CodesetSelect
                label="Schedule Type"
                codesetCode="B57"
                value={
                  signalData.scheduleType
                    ? { code: signalData.scheduleType, description: "" }
                    : null
                }
                onChange={(val) => {
                  const updated = [...signalDataArray];
                  updated[signalDataIdx].scheduleType = val.code;
                  setSignalDataArray(updated);
                }}
              />
              <FormControl>
                <FormLabel>Schedule Id</FormLabel>
                <Input
                  value={signalData.scheduleId || ""}
                  onChange={(e) => {
                    const updated = [...signalDataArray];
                    updated[signalDataIdx].scheduleId = e.target.value;
                    setSignalDataArray(updated);
                  }}
                />
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Input
                value={signalData.description || ""}
                onChange={(e) => {
                  const updated = [...signalDataArray];
                  updated[signalDataIdx].description = e.target.value;
                  setSignalDataArray(updated);
                }}
              />
            </FormControl>
          </Box>

          <ReferencesEditor
            size="sm"
            references={signalData.references || []}
            onChange={(refs) => {
              // Copy data from registerEntry into signalData
              if (signalData.registerEntry) {
                const entryData = signalData.registerEntry;
                const updated = [...signalDataArray];
                updated[signalDataIdx] = {
                  ...updated[signalDataIdx],
                  ...entryData, // Copy all fields from registerEntry
                  registerEntry: entryData, // Keep reference
                };
                setSignalDataArray(updated);
              }
              const updated = [...signalDataArray];
              updated[signalDataIdx].references = refs;
              setSignalDataArray(updated);
            }}
          />
          <ClassificationsEditor
            size="sm"
            classifications={signalData.classifications || []}
            onChange={(vals) => {
              const updated = [...signalDataArray];
              updated[signalDataIdx].classifications = vals;
              setSignalDataArray(updated);
            }}
          />
          <AmountsEditor
            size="sm"
            amounts={signalData.amounts || []}
            onChange={(vals) => {
              const updated = [...signalDataArray];
              updated[signalDataIdx].amounts = vals;
              setSignalDataArray(updated);
            }}
          />
          <DateTimesEditor
            size="sm"
            dateTimes={signalData.dateTimes || []}
            onChange={(vals) => {
              const updated = [...signalDataArray];
              updated[signalDataIdx].dateTimes = vals;
              setSignalDataArray(updated);
            }}
          />

          <HStack justify="flex-end">
            <IconButton
              icon={<HiOutlineTrash />}
              size="md"
              onClick={() => {
                const updated = signalDataArray.filter(
                  (data) => data.id !== signalData.id
                );
                setSignalDataArray(updated);
              }}
            />
          </HStack>
        </Box>
      ))}
    </Box>
  );
};

export default SignalCreator;
